import constant from "../../utils/CommonConstants";
import { updateDuration } from "../../utils/dateUtils";

const getFormattedDate = (dateStr) => {
  const date = new Date(dateStr);
  return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
};

const getCitiesByPolygons = (polygonIds, cityData) => {
  let cityList = [];
  if (polygonIds.length && cityData) {
    for (let polygon of polygonIds) {
      let city = cityData.other_cities.find((val) => val.polygon_uuid === polygon);
      if (city) {
        cityList.push(city.name);
      } else {
        let top_city = cityData.top_cities.find(
          (val) => val.polygon_uuid === polygon
        );
        if (top_city) {
          cityList.push(top_city.name);
        }
      }
    }
  }
  return cityList;
};

export const createHousingNeoApiData = (
  formData = {},
  projectFormData = {},
  projectMap,
  update = false,
  slotId
) => {
  const {
    slotDetails: {
      housing_neo_type,
      entity_type_id,
      start_date,
      end_date,
      opportunity_id,
      inner_churn_radius,
      outer_churn_radius,
      amount,
      omni_channel_amount,
      send_activation_mail = false,
      leads_count,
      polygon_id,
      entity_id,
      product_type_id,
      product_activation_id,
      notes,
      is_locality_churn,
      supply_user_uuid,
      fixed_static_listing,
      fixed_ad_slot,
      lead_exclusivity,
      event_id,
      organic_soft_target,
      organic_hard_target,
      deliver_developer_broker_leads = true,
      package_id,
    } = {},
  } = formData;
  const housingNeoId = projectMap["Neo"] || "";
  const fixedStaticListingId = projectMap["Project Fixed Static Listing"] || "";
  const fixedAdSlotId = projectMap["Fixed Ad Slot"] || "";
  const leadExclusivityId = projectMap["Lead Exclusivity"];
  const { fasCity, fslCity, ...rest } = projectFormData;
  return {
    package_details: {
      entity_type_id,
      start_date: getFormattedDate(start_date),
      end_date: getFormattedDate(end_date),
      inner_churn_radius,
      amount,
      send_activation_mail,
      outer_churn_radius,
      opportunity_id,
      entity_id,
      supply_user_uuid,
      product_type_id,
      product_activation_id,
      notes,
      is_locality_churn,
      event_id,
      organic_soft_target,
      organic_hard_target,
      deliver_developer_broker_leads,
      package_id,
    },
    promoted_products: {
      [housingNeoId]: {
        polygon_id,
        product_details: {
          housing_neo_type,
          leads_count,
          omni_channel_amount:
            omni_channel_amount === 0 ? null : omni_channel_amount,
          ...rest,
        },
      },
      ...(lead_exclusivity && { [leadExclusivityId]: {} }),
      ...(fixed_ad_slot && { [fixedAdSlotId]: { polygon_id: fasCity } }),
      ...(fixed_static_listing && {
        [fixedStaticListingId]: { polygon_id: fslCity },
      }),
    },
    ...(update && { slot_id: parseInt(slotId) }),
  };
};

export const validateCreateHousingNeoApiData = (
  apiData = {},
  questions,
  projectMap,
  updateForm
) => {
  const { package_details: slotDetails, promoted_products: promotedProducts } =
    apiData;
  let errors = [];
  const housingNeoId = projectMap["Neo"] || "";
  const {
    product_details: productDetails = {},
    polygon_id: polygonId = "",
    product_details: { omni_channel_amount: omniChannelAmount } = {},
  } = promotedProducts[housingNeoId] || {};
  const { housing_neo_type: housingNeoType } = productDetails;
  const questionFieldArray = questions.map((question) => question.fields).flat();

  for (let question of questionFieldArray) {
    if (question.required) {
      if (updateForm && question.propKey === "package_id") {
        continue;
      } else if (
        question.propKey === "start_date" &&
        slotDetails["start_date"] === getFormattedDate("")
      ) {
        errors.push(`Please enter ${question.title}`);
      } else if (
        question.propKey === "end_date" &&
        slotDetails["end_date"] === getFormattedDate("")
      ) {
        errors.push(`Please enter ${question.title}`);
      } else if (question.propKey === "polygon_id") {
        if (!polygonId) errors.push(`Please enter ${question.title}`);
      } else if (question.propKey === "am_target_polygon_id") {
        if (
          !productDetails["am_target_polygon_id"] ||
          !productDetails["am_target_polygon_id"].length
        )
          errors.push(`Please enter ${question.title}`);
      } else if (
        slotDetails[question.propKey] === undefined &&
        productDetails[question.propKey] === undefined
      ) {
        errors.push(`Please enter ${question.title}`);
      } else if (question.propKey === "opportunity_id") {
        if (!slotDetails["opportunity_id"] || !slotDetails["opportunity_id"].length)
          errors.push(`Please enter ${question.title}`);
      } else if (question.propKey === "project_ids") {
        if (!productDetails["project_ids"] || !productDetails["project_ids"].length)
          errors.push(`Please enter ${question.title}`);
        if (
          productDetails["project_ids"] &&
          productDetails["project_ids"].split(",").length < 1
        )
          errors.push("You have to enter a minimum of 3 competitor projects");
      } else if (question.propKey === "supply_user_uuid") {
        if (!slotDetails[question.propKey])
          errors.push(`Please enter ${question.title}`);
      } else if (question.propKey === "amount") {
        if (slotDetails["amount"] <= 0)
          errors.push("Please enter an amount greater than 0");
      } else if (
        question.propKey === "omni_channel_amount" &&
        omniChannelAmount < 0
      ) {
        errors.push(
          "Please enter an omni channel amount greater than or equal to 0"
        );
      } else if (question.propKey === "package_id" && !slotDetails["package_id"]) {
        errors.push(`Please enter ${question.title}`);
      }
    }
  }

  if (
    checkUndefinedValue(slotDetails, "organic_hard_target") &&
    !checkUndefinedValue(slotDetails, "organic_soft_target")
  ) {
    errors.push("Please enter value for Organic Hard Target");
  } else if (
    checkUndefinedValue(slotDetails, "organic_soft_target") &&
    !checkUndefinedValue(slotDetails, "organic_hard_target")
  ) {
    errors.push("Please enter value for Organic Soft Target");
  }

  if (getUtcTime(slotDetails["start_date"]) > getUtcTime(slotDetails["end_date"])) {
    errors.push("Start Date should be earlier than or equal to End Date");
  }
  if (housingNeoType === constant.housingNeoTypes.project_level) {
    if (
      Array.isArray(productDetails["project_ids"]) &&
      productDetails["project_ids"].indexOf(slotDetails["entity_id"]) !== -1
    )
      errors.push(
        "Please select competitor projects different than the primary project"
      );
  }
  return errors;
};

export const housingNeoSlotsParser = (
  housingNeoSlotData,
  cityData,
  projectMap = {},
  projectName
) => {
  if (Array.isArray(housingNeoSlotData))
    return housingNeoSlotData.map((slot) => {
      const {
        slot_id: slotId,
        start_date: startDate,
        end_date: endDate,
        promoted_entity_type: promotedEntity = "New Projects",
        opportunity_id: opportunityId,
        entity_id: entityId,
        polygon_uuids: polygonIds = [],
        slot_status: slotStatus,
        product_type_id,
        builder_name: sellerUuid,
        entity_id: projectId,
        polygon_name: polygonName,
        ...rest
      } = slot;
      return {
        slotId,
        startDate,
        endDate,
        promotedEntity,
        opportunityId,
        entityId,
        polygonIds: getCitiesByPolygons(polygonIds, cityData),
        slotStatus,
        productType: Object.values(constant.productTypes).reduce((res, item) => {
          const test = { ...res };
          test[item.value] = item.label;
          return test;
        }, {})[product_type_id],
        sellerUuid,
        projectId,
        polygonName,
        ...rest,
      };
    });
  const {
    start_datetime,
    end_datetime,
    promoted_entity,
    slot_product_mappings,
    locality_details,
    product_ids,
    ...rest
  } = housingNeoSlotData;
  const { promoted_entity_type_id, is_locality_churn, entity_id, supply_user_uuid } =
    promoted_entity;
  const housingNeoId = projectMap["Neo"] || "";
  const fixedStaticListingId = projectMap["Project Fixed Static Listing"] || "";
  const fixedAdSlotId = projectMap["Fixed Ad Slot"] || "";
  const leadExclusivityId = projectMap["Lead Exclusivity"];
  const {
    leads_count,
    housing_neo_type,
    am_target_polygon_id,
    min_budget,
    max_budget,
    locality_city,
    project_ids,
    omni_channel_amount = 0,
  } = slot_product_mappings[housingNeoId] || {};
  const { polygon_uuid, polygon_name } = locality_details.find(
    (item) => item.product_id === housingNeoId
  );
  const fslCityArr =
    locality_details
      .filter((item) => item.product_id === fixedStaticListingId)
      .map(({ polygon_uuid: uuid = "", polygon_name: name }) => ({
        name,
        uuid,
      })) || [];
  const fasCityArr =
    locality_details
      .filter((item) => item.product_id === fixedAdSlotId)
      .map(({ polygon_uuid: uuid = "", polygon_name: name }) => ({
        name,
        uuid,
      })) || [];
  return {
    slotDetails: {
      start_date: start_datetime,
      end_date: end_datetime,
      entity_type_id: promoted_entity_type_id,
      leads_count,
      omni_channel_amount,
      slot_product_mappings,
      promoted_entity,
      housing_neo_type,
      is_locality_churn,
      polygon_id: polygon_uuid,
      polygon_name,
      entity_id,
      fixed_ad_slot: product_ids.indexOf(fixedAdSlotId) > -1,
      fixed_static_listing: product_ids.indexOf(fixedStaticListingId) > -1,
      lead_exclusivity: product_ids.indexOf(leadExclusivityId) > -1,
      supply_user_uuid,
      projectName,
      duration: updateDuration(start_datetime, end_datetime),
      ...rest,
    },
    editProduct: {
      fasCityArr,
      fslCityArr,
      locality_city: {
        uuid: locality_city,
        name: getCitiesByPolygons([locality_city], cityData)[0],
      },
      min_budget,
      max_budget,
      project_ids,
      am_target_polygon_id:
        housing_neo_type !== constant.housingNeoTypes.locality
          ? getCitiesByPolygons(am_target_polygon_id || [], cityData).map(
              (item, index) => ({
                uuid: am_target_polygon_id[index],
                name: item,
              })
            )
          : am_target_polygon_id,
    },
  };
};

export const getSellerUuidFromList = (data = [], sellerDetails = {}) => {
  if (data && data.length > 0) {
    return data.map(({ profile_uuid, name }) => {
      const { name: sellerName, phone: sellerPhone } =
        sellerDetails[profile_uuid]?.login_details || {};
      const finalName = sellerName || name;
      return {
        uuid: profile_uuid,
        name: sellerPhone ? `${finalName} (${sellerPhone})` : finalName,
      };
    });
  }
  return [];
};

const getUtcTime = (date) => {
  const dateSplitArr = date.split("/");
  const utcFormatDate = `${dateSplitArr[1]}/${dateSplitArr[0]}/${dateSplitArr[2]}`;
  return new Date(utcFormatDate).getTime();
};

const checkUndefinedValue = (object, key) => typeof object[key] === "undefined";
